import clsx from 'clsx'
import type { ReactNode } from 'react'

/**
 * NSW Design System "In-page alert", rendered with the canonical DS markup
 * (https://designsystem.nsw.gov.au/components/in-page-alert/index.html).
 *
 * We deliberately do NOT use `nsw-ds-react`'s <Alert>: it hardcodes the title
 * as a bare <h5> (the DS uses `<p class="nsw-h5">` so an alert never injects a
 * heading into the document outline), and it renders the icon as an Iconify
 * SVG with explicit width/height attributes, which override the DS's
 * font-size-based icon sizing and so make the compact variant's smaller icon
 * impossible. Neither is overridable through its props.
 *
 * The Material Icons font is not loaded in this project, so the DS's
 * `<span class="material-icons">` glyph is rendered as the equivalent inline
 * SVG sized in `em` — visually identical, sized by the DS CSS, and (unlike
 * Iconify, which fetches from its API) present in the server-rendered HTML.
 */
export type InPageAlertType = 'info' | 'warning' | 'error' | 'success'

// Material baseline glyphs matching the DS icon per status:
// info = "info", warning = "error", error = "cancel", success = "check_circle".
const ALERT_ICON_PATH: Record<InPageAlertType, string> = {
	info: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10s10-4.48 10-10S17.52 2 12 2m1 15h-2v-6h2zm0-8h-2V7h2z',
	warning:
		'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10s10-4.48 10-10S17.52 2 12 2m1 15h-2v-2h2zm0-4h-2V7h2z',
	error: 'M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z',
	success:
		'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z',
}

export interface InPageAlertProps {
	as: InPageAlertType
	/**
	 * Rendered as the DS `nsw-h5` paragraph. In compact mode the DS has no
	 * title at all — the caller should merge it into the body as a bold lead;
	 * if it can't, passing `title` here renders it as a bold lead line instead.
	 */
	title?: string
	/** DS compact variant: tighter padding, no border, smaller icon, small text */
	compact?: boolean
	className?: string
	children?: ReactNode
	'data-testid'?: string
}

export function InPageAlert({
	as,
	title,
	compact = false,
	className,
	children,
	'data-testid': dataTestId,
	...rest
}: InPageAlertProps) {
	return (
		<div
			className={clsx(
				'nsw-in-page-alert',
				`nsw-in-page-alert--${as}`,
				compact && 'nsw-in-page-alert--compact',
				className,
			)}
			css={{ color: 'var(--nsw-text-dark)' }}
			data-testid={dataTestId}
			{...rest}
		>
			<svg
				className="nsw-material-icons nsw-in-page-alert__icon"
				focusable="false"
				aria-hidden="true"
				width="1em"
				height="1em"
				viewBox="0 0 24 24"
			>
				<path fill="currentColor" d={ALERT_ICON_PATH[as]} />
			</svg>
			<div
				className={clsx(
					'nsw-in-page-alert__content',
					compact && 'nsw-small',
				)}
			>
				{title &&
					(compact ? (
						<p className="nsw-small">
							<strong>{title}</strong>
						</p>
					) : (
						<p className="nsw-h5">{title}</p>
					))}
				{children}
			</div>
		</div>
	)
}

export default InPageAlert
